import { Box, Typography } from "@mui/material";
import { Dispatch, SetStateAction, useState } from "react";
import { FilterLiveSearch, ListContextProvider, useListController } from "react-admin";
import { PageHeader } from "@/components/common/PageHeader";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { StyledTabs, StyledTab } from "@/components/ui/mui/StyledTabs";
import { DocumentRecordType, DocumentStatus, ResourceName, ViewByDocumentType, type DocumentRead } from "@/types";
import ViewBySelect from "@/components/common/ViewBySelect";
import { DocumentQueue } from "./components/DocumentQueue";
import { ACTIVE_STATUSES, COMPLETED_STATUSES, FAILED_STATUSES } from "@/constants/documentStatus";
import { SectionCard } from "@/components/common/SectionCard";
import { getResourceIcon } from "@/utils/icons";
import { useDocumentListPolling } from "./hooks/useDocumentListPolling";
import { convertCamelCaseToTitleCase } from "@/utils/formatters/stringFormatters";
import { QuickUploadCTA } from "@/pages/landing/components/QuickUploadCTA";
import { UploadTypeDialog } from "@/pages/landing/components/UploadTypeDialog";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";

enum DocumentByCategoryTabs {
  ALL_DOCUMENTS = "allDocuments",
  POLICY_DOCUMENTS = "policyDocuments",
  CLAIM_DOCUMENTS = "claimDocuments",
  REIMBURSEMENT_DOCUMENTS = "reimbursementDocuments",
}

/** Explicit tab ordering — avoids relying on Object.values() enum order */
const CATEGORY_TABS = [
  DocumentByCategoryTabs.ALL_DOCUMENTS,
  DocumentByCategoryTabs.POLICY_DOCUMENTS,
  DocumentByCategoryTabs.CLAIM_DOCUMENTS,
  DocumentByCategoryTabs.REIMBURSEMENT_DOCUMENTS,
] as const;

/** Each category tab maps to a single `record_type` server-side filter (All = unfiltered). */
const RECORD_TYPE_BY_TAB: Record<DocumentByCategoryTabs, DocumentRecordType | undefined> = {
  [DocumentByCategoryTabs.ALL_DOCUMENTS]: undefined,
  [DocumentByCategoryTabs.POLICY_DOCUMENTS]: DocumentRecordType.POLICIES,
  [DocumentByCategoryTabs.CLAIM_DOCUMENTS]: DocumentRecordType.STOPLOSS_CLAIMS,
  [DocumentByCategoryTabs.REIMBURSEMENT_DOCUMENTS]: DocumentRecordType.REIMBURSEMENTS,
};

// `upload_failed` is a frontend-only status — it is NOT a member of the backend
// document_status enum — so the "Failed Uploads" bucket maps to the server-side
// failure states only. Sending upload_failed would error the enum cast (#5221).
const SERVER_FAILED_STATUSES = FAILED_STATUSES.filter((s) => s !== DocumentStatus.UPLOAD_FAILED);

/** Each ViewBy bucket maps to the set of server-side statuses OR-matched via `status` ANY. */
const STATUS_FILTER_BY_VIEW: Record<ViewByDocumentType, DocumentStatus[]> = {
  [ViewByDocumentType.NONE]: [],
  [ViewByDocumentType.IN_PROCESS]: ACTIVE_STATUSES,
  [ViewByDocumentType.NEEDS_REVIEW]: [DocumentStatus.REVIEW_REQUIRED],
  [ViewByDocumentType.FAILED_UPLOADS]: SERVER_FAILED_STATUSES,
  [ViewByDocumentType.COMPLETED]: COMPLETED_STATUSES,
};

const VIEW_BY_OPTIONS = [
  ViewByDocumentType.NONE,
  ViewByDocumentType.IN_PROCESS,
  ViewByDocumentType.NEEDS_REVIEW,
  ViewByDocumentType.FAILED_UPLOADS,
  ViewByDocumentType.COMPLETED,
];

const sameStatusSet = (a: DocumentStatus[], b: DocumentStatus[]) =>
  a.length === b.length && a.every((s) => b.includes(s));

export const DocumentList = () => {
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);

  const { canCreate } = useResourcePermissions(ResourceName.DOCUMENTS);
  const DocumentsIcon = getResourceIcon(ResourceName.DOCUMENTS);

  // Server-side list: the dataProvider owns fetching, sort, pagination, and
  // filtering (q + record_type + status). Replaces the prior fetch-all-then
  // filter-in-memory approach so search/tabs span all documents, not one page.
  const listContext = useListController<DocumentRead>({
    resource: ResourceName.DOCUMENTS,
    sort: { field: "uploaded_datetime", order: "DESC" },
    perPage: 25,
  });

  const {
    data: documents,
    total,
    isPending: loading,
    page,
    setPage,
    perPage,
    setPerPage,
    filterValues,
    setFilters,
    displayedFilters,
    refetch: refetchDocuments,
  } = listContext;

  useDocumentListPolling(documents);

  // Derive the active tab / status bucket from the live filter so the controls
  // stay in sync with the URL-backed list state (single source of truth).
  const recordTypeFilter = filterValues.record_type as DocumentRecordType | undefined;
  const selectedCategory =
    CATEGORY_TABS.find((tab) => RECORD_TYPE_BY_TAB[tab] === recordTypeFilter) ?? DocumentByCategoryTabs.ALL_DOCUMENTS;

  const statusFilter = (filterValues.status as DocumentStatus[] | undefined) ?? [];
  const viewByStatus =
    VIEW_BY_OPTIONS.find((view) => sameStatusSet(STATUS_FILTER_BY_VIEW[view], statusFilter)) ?? ViewByDocumentType.NONE;

  const handleCategoryChange = (index: number) => {
    const recordType = RECORD_TYPE_BY_TAB[CATEGORY_TABS[index]];
    const next = { ...filterValues };
    if (recordType) {
      next.record_type = recordType;
    } else {
      delete next.record_type;
    }
    setFilters(next, displayedFilters);
  };

  const handleStatusChange = (value: string) => {
    const statuses = STATUS_FILTER_BY_VIEW[value as ViewByDocumentType] ?? [];
    const next = { ...filterValues };
    if (statuses.length > 0) {
      next.status = statuses;
    } else {
      delete next.status;
    }
    setFilters(next, displayedFilters);
  };

  const tabIndex = CATEGORY_TABS.indexOf(selectedCategory);

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Documents"
        customSubtitle="Manage and review uploaded policy, claim and reimbursement files"
        hideSectionTitle
        headerIcon={<DocumentsIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <PageContent>
        {canCreate && (
          <Box sx={{ mb: 3 }}>
            <QuickUploadCTA onClick={() => setUploadDialogOpen(true)} />
          </Box>
        )}

        {/* Search and Filters — search is sent to the server as `q` */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Box sx={{ width: 300 }}>
            <FilterLiveSearch source="q" placeholder="Search documents..." fullWidth />
          </Box>

          <Box sx={{ width: 200, height: "auto" }}>
            <ViewBySelect
              disabled={loading}
              viewBy={{
                title: "View Documents by Status",
                selection: viewByStatus,
                onChange: handleStatusChange as Dispatch<SetStateAction<string>>,
                options: VIEW_BY_OPTIONS.map((optionType) => ({
                  optionTitle: convertCamelCaseToTitleCase(optionType),
                  optionType,
                })),
              }}
            />
          </Box>
        </Box>

        {/* Category Tabs — each sets the server-side record_type filter */}
        <StyledTabs value={tabIndex} onChange={(_e, i: number) => handleCategoryChange(i)} sx={{ mb: 2 }}>
          <StyledTab label="All Documents" disabled={loading} />
          <StyledTab label="Policy Documents" disabled={loading} />
          <StyledTab label="Claim Documents" disabled={loading} />
          <StyledTab label="Reimbursement Documents" disabled={loading} />
        </StyledTabs>

        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {!loading && typeof total === "number" ? `${total} ${total === 1 ? "document" : "documents"}` : " "}
          </Typography>
        </Box>

        <Box>
          <TabContent>
            <SectionCard>
              <DocumentQueue
                documents={documents}
                isLoading={loading}
                onRefresh={refetchDocuments}
                serverSide
                totalCount={total}
                page={page - 1}
                onPageChange={(newPage) => setPage(newPage + 1)}
                rowsPerPage={perPage}
                onRowsPerPageChange={(newPerPage) => setPerPage(newPerPage)}
              />
            </SectionCard>
          </TabContent>
        </Box>
      </PageContent>

      {canCreate && (
        <UploadTypeDialog
          open={uploadDialogOpen}
          onClose={() => setUploadDialogOpen(false)}
          // Wrapped: the callback now receives the created documents, which
          // must not reach refetch() as its options object.
          onUploadComplete={() => void refetchDocuments()}
        />
      )}
    </ListContextProvider>
  );
};
